/**
 * Tool display contract for `ToolCallMessage`.
 *
 * Single source of truth for what a tool is lives on the tRPC procedure
 * itself (`.meta({ aiTool: { description, category, keywords, readonly } })`).
 * That metadata flows to the AI agent via the registry walker in
 * `apps/api/src/ai/registry/build.ts` — adding a new AI-callable tool
 * needs only the backend tag, never an edit here.
 *
 * The renderer still needs *display* affordances that don't serialise
 * across the wire (lucide icon refs, output-payload formatters). Those
 * fall into two buckets:
 *
 *   1. `deriveToolConfig(toolId)` — generic, used for every tool by
 *      default. Inflects "customers.list" → "List customers", picks an
 *      icon from the bounded-context prefix, and produces summaries by
 *      shape-introspecting the output (`items[]` → "Found N",
 *      `totalPaise` → "₹X", `id` → "Created", …).
 *
 *   2. `TOOL_CONFIGS` — explicit per-tool overrides. Empty by design —
 *      add an entry ONLY when a tool genuinely needs a bespoke label,
 *      icon, or summary the generic renderer can't produce. Most tools
 *      should never need one.
 *
 * Consumers should call `resolveToolConfig(toolId)` — it returns the
 * override when present, falls through to the derived config otherwise.
 */
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  ClipboardCheck,
  FileText,
  Gauge,
  Globe,
  History,
  Landmark,
  Layers,
  Package,
  Receipt,
  ReceiptText,
  Search,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";

export interface ToolConfig {
  /** Status text used when no output detail or input detail is available. */
  text?: string;
  /** Short label shown when nothing else applies; otherwise the icon fronts the row. */
  label?: string;
  /** Icon shown next to the row when the tool succeeded. */
  icon?: LucideIcon;
  /** Inline detail derived from the tool's args while streaming. */
  getDetail?: (input: Record<string, unknown>) => string | undefined;
  /** Inline summary derived from the tool's output once it completes. */
  getOutputDetail?: (
    output: Record<string, unknown> | string | null,
    input?: Record<string, unknown>,
  ) => string | undefined;
}

/**
 * Explicit per-tool overrides. Add an entry only when the generic renderer
 * can't produce a good label/icon/summary — the entry wins over the derived
 * config.
 */
export const TOOL_CONFIGS: Record<string, ToolConfig> = {
  // ── Report-builder agent (/reports/new) ─────────────────────────────

  propose_report: {
    label: "Drafting report",
    icon: BarChart3,
    getDetail: (input) => {
      const title = str(input.title);
      return title ? `Drafting "${title}"` : "Drafting report";
    },
    getOutputDetail: (output, input) => {
      const title = str(input?.title);
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (o.ok === true && typeof o.rowCount === "number") {
          const rows = `${o.rowCount} row${o.rowCount === 1 ? "" : "s"}`;
          return title ? `"${title}" — ${rows}` : `Ran the report — ${rows}`;
        }
        if (o.ok === false) return "Query needs a fix — retrying";
      }
      return title ? `Drafted "${title}"` : "Drafted report";
    },
  },

  save_report: {
    label: "Saving report",
    icon: BarChart3,
    getOutputDetail: (output) => {
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (o.ok === true && typeof o.reportKey === "string") return `Saved as ${o.reportKey}`;
        if (o.ok === false) return "Save failed";
      }
      return "Saved";
    },
  },

  update_report: {
    label: "Updating report",
    icon: BarChart3,
    getOutputDetail: (output) => {
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (o.ok === true && typeof o.reportKey === "string") return `Updated ${o.reportKey}`;
        if (o.ok === false) return "Update failed";
      }
      return "Updated";
    },
  },

  // ── System meta-tools ────────────────────────────────────────────────

  tool_search: {
    label: "Searching for tools",
    icon: Search,
    // getDetail returns the complete loading phrase so no colon-joined prefix needed.
    getDetail: (input) => {
      const q = str(input.query) ?? str(input.q);
      return q ? `Searching for "${q}"` : "Searching for tools";
    },
    getOutputDetail: (output, input) => {
      const q = str(input?.query) ?? str(input?.q);
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (Array.isArray(o.results)) {
          const n = o.results.length;
          if (n === 0) return q ? `No tools found for "${q}"` : "No tools found";
          return q
            ? `Found ${n} tool${n === 1 ? "" : "s"} for "${q}"`
            : `Found ${n} tool${n === 1 ? "" : "s"}`;
        }
      }
      return q ? `Searched for "${q}"` : "Done";
    },
  },

  tool_load: {
    label: "Loading tool",
    icon: Layers,
    getDetail: (input) => {
      const ids = Array.isArray(input.toolIds) ? (input.toolIds as string[]) : [];
      if (ids.length > 0) {
        const names = ids.map((id) => id.replace(/_/g, "."));
        if (ids.length === 1) return `Loading ${names[0]}`;
        const shown = names.slice(0, 2).join(", ");
        return ids.length > 2 ? `Loading ${shown} +${ids.length - 2}` : `Loading ${shown}`;
      }
      const single = str(input.toolId) ?? str(input.id);
      return single ? `Loading ${single.replace(/_/g, ".")}` : "Loading tool";
    },
    getOutputDetail: (output) => {
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (Array.isArray(o.loaded)) {
          const ids = o.loaded as string[];
          const n = ids.length;
          if (n === 0) return "Already loaded";
          const names = ids.map((id: string) => id.replace(/_/g, "."));
          if (n === 1) return `Loaded ${names[0]}`;
          const shown = names.slice(0, 2).join(", ");
          return n > 2 ? `Loaded ${shown} +${n - 2}` : `Loaded ${shown}`;
        }
      }
      return "Ready";
    },
  },

  read_attachments: {
    label: "Reading file",
    icon: FileText,
    getDetail: (input) => {
      const id = str(input.attachmentId);
      return id ? `Reading ${id}` : "Listing attachments";
    },
    getOutputDetail: (output) => {
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (Array.isArray(o.attachments)) {
          const n = o.attachments.length;
          return n === 0 ? "No attachments" : `${n} attachment${n === 1 ? "" : "s"}`;
        }
        if (typeof o.chars === "number") {
          return `Read ${o.chars.toLocaleString("en-IN")} chars`;
        }
        if (str(o.attachmentId)) return `Read — ${o.attachmentId}`;
      }
      return "File read";
    },
  },

  http_request: {
    label: "Web request",
    icon: Globe,
    getDetail: (input) => {
      const url = str(input.url);
      if (url) {
        try {
          return `Requesting ${new URL(url).hostname}`;
        } catch {
          return `Requesting ${url.slice(0, 50)}`;
        }
      }
      return "Making web request";
    },
    getOutputDetail: (output) => {
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (typeof o.status === "number") return `Response ${o.status}`;
        if (typeof o.ok === "boolean") return o.ok ? "Request succeeded" : "Request failed";
      }
      return "Response received";
    },
  },
};

/**
 * Public lookup. Falls through to the derived config when there is no
 * explicit override, so the renderer always gets a populated config.
 */
export function resolveToolConfig(toolId: string): ToolConfig {
  return TOOL_CONFIGS[toolId] ?? deriveToolConfig(toolId);
}

// ── Derivation primitives ─────────────────────────────────────────

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function outputMessage(output: Record<string, unknown> | string | null): string | undefined {
  if (typeof output === "string") return output;
  if (output && typeof output === "object") {
    return str(output.message) ?? str(output.summary);
  }
  return undefined;
}

/**
 * Render integer-paise (either bigint, number, or numeric-string —
 * tool payloads carry it in all three shapes) as an ₹-amount with
 * thousands separators. Used by both input and output detail
 * extractors so monetary tools read at a glance.
 */
function rupees(paise: unknown): string | undefined {
  if (paise === null || paise === undefined) return undefined;
  let n: number | undefined;
  if (typeof paise === "string" && /^-?\d+$/.test(paise)) n = Number(paise);
  else if (typeof paise === "number") n = paise;
  else if (typeof paise === "bigint") n = Number(paise);
  if (n === undefined || !Number.isFinite(n)) return undefined;
  return `₹${(n / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

/**
 * Strip a trailing "s" to render the bounded-context name in the
 * singular when the verb implies a single record. Works for every
 * bounded-context name we ship today (customers → customer, invoices
 * → invoice, bankAccounts → bank account, …). Add an irregular-noun
 * map only if a future module breaks the rule.
 */
function singularize(noun: string): string {
  return noun.endsWith("s") ? noun.slice(0, -1) : noun;
}

const SPLIT_CAMEL = /([a-z])([A-Z])/g;
function humaniseCamel(s: string): string {
  return s.replace(SPLIT_CAMEL, "$1 $2").toLowerCase();
}

/**
 * Verbs whose tool reads or writes a single record. The first word of
 * the procedure verb (e.g. `createDraft` → `create`) is matched here
 * to decide whether to render the noun singular ("Create customer")
 * or plural ("List customers").
 */
const SINGULAR_VERBS = new Set([
  "create",
  "update",
  "archive",
  "delete",
  "issue",
  "record",
  "post",
  "send",
  "void",
  "get",
  "clone",
  "transition",
]);

/**
 * Map bounded-context prefix → display icon. Adding a new tool whose
 * prefix is already listed gets the right icon for free; a brand-new
 * context just needs one line here.
 */
const ICON_BY_PREFIX: Record<string, LucideIcon> = {
  // Sales
  customers: Users,
  invoices: ReceiptText,
  creditNotes: ReceiptText,
  items: Package,
  receipts: Wallet,
  reports: BarChart3,
  // Purchases
  bills: Receipt,
  vendors: Users,
  vendorPayments: Wallet,
  // Money
  bankAccounts: Landmark,
  cashMovements: Wallet,
  bankRecon: Layers,
  statementImports: FileText,
  petty: Wallet,
  // Compliance
  gstReturns: BarChart3,
  tds: BarChart3,
  // Cross-cutting
  briefings: Gauge,
  auditLog: History,
  approvals: ClipboardCheck,
  // Meta-tools (snake_case, looked up by the full id)
  tool_search: Layers,
  tool_load: Layers,
  http_request: Wrench,
  read_attachments: FileText,
  web_search: Layers,
  get_news: FileText,
  scrape_webpage: FileText,
};

/**
 * Build a default config from the tool id alone. Used whenever
 * `TOOL_CONFIGS` has no explicit override — i.e. for every current
 * Flint tool today.
 */
export function deriveToolConfig(toolId: string): ToolConfig {
  // Dotted form ("vendors.list") is canonical; underscore form ("vendors_list")
  // is the OpenAI function-name wire format — split both the same way.
  const parts = toolId.includes(".")
    ? toolId.split(".")
    : (() => {
        const idx = toolId.indexOf("_");
        return idx > 0 ? [toolId.slice(0, idx), toolId.slice(idx + 1)] : [toolId];
      })();
  const head = parts[0] ?? toolId;
  const verbParts = parts.slice(1);

  // Label + noun (kept for output detail enrichment below).
  let label: string;
  let nounHuman: string;
  if (verbParts.length === 0) {
    // snake_case meta-tool (`tool_search`, `http_request`, …).
    label = capitalize(head.replace(/_/g, " "));
    nounHuman = label.toLowerCase();
  } else {
    const verb = humaniseCamel(verbParts.join(" "));
    const firstVerb = (verb.split(" ")[0] ?? "").replace(/[^a-z]/g, "");
    nounHuman = humaniseCamel(head);
    const noun = SINGULAR_VERBS.has(firstVerb) ? singularize(nounHuman) : nounHuman;
    label = capitalize(`${verb} ${noun}`);
  }

  const icon = ICON_BY_PREFIX[head] ?? ICON_BY_PREFIX[toolId] ?? Wrench;

  return {
    label,
    // Setting text = label means loading shows "List vendors: Sundaram"
    // rather than just "Sundaram" (which had no context).
    text: label,
    icon,
    getDetail: genericInputDetail,
    getOutputDetail: (output, input) => {
      // Enrich paged-list counts with the noun: "Found 3 vendors" not "Found 3".
      if (output && typeof output === "object" && !Array.isArray(output)) {
        const o = output as Record<string, unknown>;
        if (Array.isArray(o.items)) {
          const n = o.items.length;
          return n === 0
            ? `No ${nounHuman} found`
            : `Found ${n} ${n === 1 ? singularize(nounHuman) : nounHuman}`;
        }
      }
      return genericOutputDetail(output, input);
    },
  };
}

/**
 * One short line summarising the tool input — usually echoes a name,
 * query, or amount the user can recognise. Returns undefined when the
 * input has no obvious headline field; the renderer then falls back
 * to the label alone.
 */
function genericInputDetail(input: Record<string, unknown>): string | undefined {
  return (
    str(input.q) ??
    str(input.query) ??
    str(input.search) ??
    str(input.name) ??
    str(input.docNumber) ??
    str(input.vendorDocNumber) ??
    str(input.status) ??
    str(input.tab) ??
    rupees(input.amountPaise) ??
    rupees(input.totalPaise) ??
    extractFromIds(input)
  );
}

function extractFromIds(input: Record<string, unknown>): string | undefined {
  if (Array.isArray(input.ids) && input.ids.length > 0) {
    const first = input.ids[0];
    if (typeof first === "string") {
      return input.ids.length > 1 ? `${first} (+${input.ids.length - 1} more)` : first;
    }
  }
  return undefined;
}

/**
 * One short line summarising the tool output — shape-introspected so
 * any tool that returns a familiar payload (paged list, aggregate,
 * creation result) gets a sensible summary without per-tool wiring.
 */
function genericOutputDetail(
  output: Record<string, unknown> | string | null,
  input?: Record<string, unknown>,
): string | undefined {
  if (output === null || output === undefined) return undefined;
  if (typeof output === "string") return output;
  if (typeof output !== "object") return undefined;

  // Creation result: { id: "..." } and not much else.
  if (typeof output.id === "string" && Object.keys(output).length <= 3) {
    const noun = input
      ? str(input.name) ?? str(input.vendorDocNumber) ?? str(input.docNumber)
      : undefined;
    return noun ? `Created — ${noun}` : "Created";
  }

  // Paged list: { items: [...] }.
  if (Array.isArray(output.items)) {
    const n = output.items.length;
    return n === 0 ? "No matches" : `Found ${n}`;
  }

  // `tool_search` style: { results: [...] }.
  if (Array.isArray(output.results)) {
    const n = output.results.length;
    return n === 0 ? "No matches" : `${n} match${n === 1 ? "" : "es"}`;
  }

  // `tool_load` style: { loaded: [...] }.
  if (Array.isArray(output.loaded)) {
    const n = output.loaded.length;
    return n === 0 ? "Nothing new" : `Loaded ${n} tool${n === 1 ? "" : "s"}`;
  }

  // Single-record `get` payload — common shape:
  //   { invoice: { docNumber, totalPaise }, ... }
  // Walk the first sub-object to pull a doc number + amount.
  for (const value of Object.values(output)) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const rec = value as Record<string, unknown>;
      const num = str(rec.docNumber);
      const amt = rupees(rec.totalPaise);
      const joined = [num, amt].filter(Boolean).join(" · ");
      if (joined) return joined;
    }
  }

  // Aggregate (aging, concentration, snapshot, …).
  const total =
    rupees(output.totalOutstandingPaise) ??
    rupees(output.outstandingPaise) ??
    rupees(output.totalPaise);
  if (total) {
    const ctx = Array.isArray(output.rows)
      ? `${output.rows.length} row${output.rows.length === 1 ? "" : "s"}`
      : Array.isArray(output.buckets)
        ? `${output.buckets.length} bucket${output.buckets.length === 1 ? "" : "s"}`
        : undefined;
    return ctx ? `${total} · ${ctx}` : total;
  }

  // Counts: { total: N, archived: N, ... }.
  if (typeof output.total === "number") {
    return typeof output.archived === "number"
      ? `${output.total} active · ${output.archived} archived`
      : `${output.total} total`;
  }

  // Activity feeds: { entries: [...] }.
  if (Array.isArray(output.entries)) {
    const n = output.entries.length;
    return `${n} event${n === 1 ? "" : "s"}`;
  }

  return outputMessage(output);
}

// ── Fallbacks used by the renderer when nothing matches at all ────

/**
 * Humanise an unregistered tool id. Used by the renderer only as a
 * last-resort safety net — `deriveToolConfig` already produces a
 * better label for every shape we ship. Takes the leaf of dotted ids
 * so the fallback reads as an action, not a path.
 */
export function formatToolName(name: string): string {
  const leaf = name.includes(".") ? name.split(".").pop() ?? name : name;
  return leaf
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
}

/**
 * Common input fields that carry a useful short detail, ordered by
 * usefulness. The first match wins. Retained for the renderer's
 * fallback chain — kept in sync with `genericInputDetail` above.
 */
const DETAIL_FIELDS = [
  "query",
  "name",
  "title",
  "subject",
  "status",
  "url",
  "to",
  "filename",
  "file_path",
  "filePath",
  "path",
  "command",
  "topic",
  "keyword",
  "prompt",
  "instruction",
  "email",
] as const;

export function extractToolDetail(input: Record<string, unknown>): string | undefined {
  for (const field of DETAIL_FIELDS) {
    const value = input[field];
    if (typeof value === "string" && value.length > 0 && value.length < 120) return value;
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0];
      if (typeof first === "string" && first.length > 0 && first.length < 120) {
        return value.length > 1 ? `${first} (+${value.length - 1} more)` : first;
      }
    }
  }
  return undefined;
}

/** Generic icon for tools without a registered config. */
export const GENERIC_TOOL_ICON: LucideIcon = Wrench;
